import { CALENDAR_SYNC_URL, CALENDAR_SYNC_HEADERS } from '../../config.js'

const TABLE = 'calendar_events'
// Date.now() is 13 digits in 2026; Supabase autoincrement ids are <13 digits
// for the foreseeable future. Anything ≥ this is an optimistic local id.
const OPTIMISTIC_ID_FLOOR = 1_000_000_000_000
const isOptimisticId = (id) => typeof id !== 'number' || id >= OPTIMISTIC_ID_FLOOR
const nowIso = () => new Date().toISOString()

// `date` + `startTime` are local wall-clock (the user dragged "8:30 on Apr 9"
// in their own timezone). Resolve them into a UTC instant via `new Date(...)`,
// which interprets the literal as local. This makes start_at the canonical
// timestamp the rest of the pipeline (Google sync push, _rowToEvent) reads.
const _localToUtcIso = (date, time) => {
  if (!date || !time) return null
  const d = new Date(`${date}T${time}:00`)
  return isNaN(d) ? null : d.toISOString()
}

// Add one calendar day to a 'YYYY-MM-DD' string. Used to compute end_date
// for all-day events (Google represents "1 day all-day" as start.date=X,
// end.date=X+1 — a half-open interval).
const _addDay = (ymd) => {
  if (!ymd) return null
  const [y, m, d] = ymd.split('-').map(Number)
  const next = new Date(y, m - 1, d + 1)
  const pad = n => String(n).padStart(2, '0')
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
}

const _toRow = (ev, currentUserEmail) => {
  const allDay = !!ev.allDay
  const endDate = allDay ? _addDay(ev.date) : (ev.endDate || null)
  return {
    ...(typeof ev.id === 'number' && !isOptimisticId(ev.id) ? { id: ev.id } : {}),
    title: ev.title || '(no title)',
    date: ev.date,
    end_date: endDate,
    // All-day events leave start_time/end_time NULL — the sync server's
    // pushUser keys off `!start_time` to send Google's `start: { date }`
    // shape instead of `dateTime`.
    start_time: allDay ? null : (ev.startTime || null),
    end_time: allDay ? null : (ev.endTime || null),
    // Preserve original UTC timestamps when available (editing without time change).
    // Re-convert only when missing (new event) or times were actually modified.
    start_at: ev.startAt || (allDay
      ? _localToUtcIso(ev.date, '00:00')
      : _localToUtcIso(ev.date, ev.startTime)),
    end_at: ev.endAt || (allDay
      ? _localToUtcIso(endDate, '00:00')
      : _localToUtcIso(ev.endDate || ev.date, ev.endTime)),
    color: ev.color || null,
    type: ev.type || 'meeting',
    description: ev.description || null,
    invitees: ev.invitees || [],
    organizer_email: ev.creatorEmail || currentUserEmail,
    google_event_id: ev.googleEventId || null,
    google_calendar_id: ev.googleCalendarId || null,
    updated_at: nowIso(),
    deleted: false,
    dirty: true
  }
}

// Fire-and-forget — UI shouldn't block on the Google round-trip.
const triggerSync = () => {
  fetch(CALENDAR_SYNC_URL, { method: 'POST', headers: CALENDAR_SYNC_HEADERS }).catch(() => {})
}

const _logErr = (scope, err) => console.error(`[${scope}]`, err?.message || err)

export const saveEventToSupabase = async function (ev) {
  const fetch = this.context?.fetch
  if (!fetch?.upsert) {
    _logErr('saveEventToSupabase', 'context.fetch.upsert missing')
    return null
  }
  const row = _toRow(ev, this.state?.root?.currentUserEmail)
  try {
    const result = await fetch.upsert({ from: TABLE, data: row })
    if (result?.error) { _logErr('saveEventToSupabase', result.error); return null }
    triggerSync()
    // Notify invitees about the calendar event
    const me = this.state?.root?.currentUserEmail
    const invitees = ev.invitees || []
    if (invitees.length && me) {
      try {
        const { createNotifications } = await import('../../../shared/functions/notifications/createNotification.js')
        createNotifications.call(this, invitees.filter(e => e !== me), {
          type: 'calendar_invite',
          title: `${ev.title || 'New event'} — you're invited`,
          body: ev.description?.substring(0, 100) || '',
          href: '/calendar'
        })
      } catch (e) { _logErr('saveEventToSupabase:notify', e) }
    }
    return result?.data?.[0]?.id ?? null
  } catch (e) {
    _logErr('saveEventToSupabase', e)
    return null
  }
}

// Soft-delete: flip `deleted` + `dirty` so the sync server can push the
// tombstone to Google Calendar before the row is physically removed.
export const deleteEventFromSupabase = async function (eventId) {
  if (isOptimisticId(eventId)) return true
  const fetch = this.context?.fetch
  if (!fetch?.update) {
    _logErr('deleteEventFromSupabase', 'context.fetch.update missing')
    return false
  }
  try {
    const result = await fetch.update({
      from: TABLE,
      params: { id: eventId },
      data: { deleted: true, dirty: true, updated_at: nowIso() }
    })
    if (result?.error) { _logErr('deleteEventFromSupabase', result.error); return false }
    triggerSync()
    return true
  } catch (e) {
    _logErr('deleteEventFromSupabase', e)
    return false
  }
}

// Update shared fields (type, description, invitees) on all rows with the same
// google_event_id. Called when editing an event that's shared across calendars.
// Update shared fields on all copies of the same event. Matches by
// google_event_id first, then also by title+start_at to catch forwarded
// copies that have a different Google event ID.
export const updateSharedEventFields = async function (ev, fields) {
  const fetch = this.context?.fetch
  if (!fetch?.update) return
  const data = { updated_at: nowIso(), dirty: true }
  if (fields.type !== undefined) data.type = fields.type
  if (fields.description !== undefined) data.description = fields.description
  if (fields.invitees !== undefined) data.invitees = fields.invitees
  try {
    // Update by google_event_id
    if (ev.googleEventId) {
      await fetch.update({
        from: TABLE,
        params: { google_event_id: ev.googleEventId, deleted: false },
        data
      })
    }
    // Also update by title + start_at to catch forwarded copies
    if (ev.title && ev.startAt) {
      await fetch.update({
        from: TABLE,
        params: { title: ev.title, start_at: ev.startAt, deleted: false },
        data
      })
    }
    triggerSync()
  } catch (e) {
    _logErr('updateSharedEventFields', e)
  }
}

export { triggerSync as triggerCalendarSync }
